import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiGet } from "../../../api/apiGet";
import { apiPost } from "../../../api/apiPost";
import { extractWarehouseInfo } from "../../../helpers/helperFunctions";

export const getOrderHistory = createAsyncThunk(
  "orderSlice/getOrderHistory",
  async (_, thunkAPI) => {
    try {
      const response = await apiGet.getOrderHistory();
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
      });
    }
  },
);

export const getOrderItemRepresentation = createAsyncThunk(
  "orderSlice/getOrderItemRepresentation",
  async (_, thunkAPI) => {
    try {
      const response = await apiGet.getOrderItemRepresentation();
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
      });
    }
  },
);

export const getTrackFee = createAsyncThunk(
  "orderSlice/getTrackFee",
  async (_, thunkAPI) => {
    try {
      const response = await apiGet.getTrackFee();
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
      });
    }
  },
);

export const getOrderById = createAsyncThunk(
  "orderSlice/getOrderById",
  async ({ order_num }, thunkAPI) => {
    try {
      const response = await apiGet.getOrderById({ order_num });
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
      });
    }
  },
);

export const collectShippingInfo = createAsyncThunk(
  "orderSlice/collectShippingInfo",
  async (_, thunkAPI) => {
    try {
      const storeStates = thunkAPI.getState();
      const {
        orders: { loadedOrderItems },
        customer: {
          customer: {
            shipping_address: { zip_code },
          },
          address_classification,
        },
        cart: {
          cart: { cart_total, total_weight },
        },
      } = storeStates;

      const products = loadedOrderItems.map((item) => {
        return {
          product_code: item.product.product_code,
          qty_ordered: item.qty_ordered,
          weight: item.product.shipping_weight,
        };
      });

      const payload = {
        products,
        zip_code,
        total: cart_total,
        weight: total_weight,
        address_classification,
      };

      let submission;
      try {
        submission = await apiPost.collectShippingInfoAsync(payload);
      } catch (submissionError) {
        // Keep mixed-version deployments usable while the async endpoint rolls
        // out. Other errors should surface normally rather than double-rating.
        if (![404, 405].includes(submissionError?.response?.status)) {
          throw submissionError;
        }
        const response = await apiPost.collectShippingInfo(payload);
        response.data.last_rated_weight = total_weight;
        return response.data;
      }

      const jobId = submission.data.job_id;
      const deadline = Date.now() + 30000;
      while (Date.now() < deadline) {
        const response = await apiGet.getAsyncJob(jobId, thunkAPI.signal);
        const job = response.data;
        thunkAPI.dispatch(
          setShippingRatingProgress({
            progress: job.progress || 0,
            status: job.status,
          }),
        );

        if (job.status === "completed") {
          const result = job.result || {};
          result.last_rated_weight = total_weight;
          return result;
        }
        if (job.status === "failed") {
          throw new Error(
            (job.error || "Shipping rating failed.").split("\n")[0],
          );
        }

        await new Promise((resolve) => setTimeout(resolve, 400));
      }
      const timeoutError = new Error(
        "Shipping rating timed out. Please try again.",
      );
      timeoutError.code = "ECONNABORTED";
      throw timeoutError;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
        timedOut: err?.code === "ECONNABORTED",
        message:
          err?.code === "ECONNABORTED"
            ? "Shipping rating timed out. Please try again."
            : err?.message || "Error collecting shipping information.",
      });
    }
  },
  {
    // Single-flight guard: skip if a rate call is already in progress. The
    // .pending reducer sets collectingShippingInfo synchronously before the
    // async body yields, so a duplicate dispatch (React StrictMode double-invoke,
    // rapid re-renders) is rejected before it makes a second ~20s backend call.
    condition: (_, { getState }) => {
      if (getState().orders.collectingShippingInfo) {
        return false;
      }
    },
  },
);

export const sendToClient = createAsyncThunk(
  "orders/sendToClient",
  async ({ recipients, customer_order_num, isSubOrder = true }, thunkAPI) => {
    try {
      await apiPost.sendOrderByEmailFax({
        isSubOrder,
        faxEmailData: {
          recipients: recipients,
        },
        id: customer_order_num,
      });
    } catch (err) {}
  },
);

export const doInvoiceAction = createAsyncThunk(
  "invoices/print",
  async (
    {
      transactionNum,
      transactionNumType,
      deliveryMethod,
      emailAddress,
      useOverride,
      showPricing,
    },
    thunkAPI,
  ) => {
    try {
      const response = await apiGet.getInvoice(
        transactionNum,
        transactionNumType,
        deliveryMethod,
        emailAddress,
        useOverride,
        showPricing,
      );
      if (response.data === 0) {
        throw new Error("No orders found for the entered parameters.");
      }

      if (deliveryMethod === "file" || deliveryMethod === "pdf") {
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement("a");
        const today = new Date();

        // Get the date components
        const year = today.getFullYear();
        const month = today.getMonth() + 1; // JavaScript months are 0-indexed
        const day = today.getDate();

        // Format the date string
        const formattedDate = `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;

        link.href = url;
        link.setAttribute(
          "download",
          deliveryMethod === "pdf"
            ? "Invoice " + formattedDate + ".pdf"
            : "Invoices " + formattedDate + ".zip",
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      return "Success";
    } catch (err) {
      if (err.message === "No orders found for the entered parameters.") {
        return thunkAPI.rejectWithValue({
          axiosError: err,
          customMsg: err.message,
        });
      }
    }
  },
);

export const createOrder = createAsyncThunk(
  "orderSlice/createOrder",
  async (_, thunkAPI) => {
    try {
      const storeStates = thunkAPI.getState();
      const {
        customer: {
          customer: { shipping_address },
          isDropShip,
          customerPo,
          address_classification,
        },
        orders: {
          selectedReduxRate,
          shippingInfo: { combinations, preferred_rate },
          holdDate,
          special_instructions,
        },
      } = storeStates;

      // Extract warehouse information using the helper function
      const { rateObject } = extractWarehouseInfo(
        selectedReduxRate,
        preferred_rate,
        combinations,
      );

      const response = await apiPost.createOrder({
        shipping_address,
        isDropShip,
        rate: rateObject,
        customer_po: customerPo,
        combinations,
        preferred_rate,
        hold_date: holdDate,
        address_classification,
        special_instructions,
      });
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        err: err?.response?.data,
      });
    }
  },
);

export const getOrderListByParameters = createAsyncThunk(
  "orderSlice/getOrdersByParemeters",
  async (params, thunkAPI) => {
    try {
      const resp = await apiGet.getOrdersList({
        ...params,
      });

      return resp;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        axiosError: err,
        customMsg: "Error getting the list of orders.",
      });
    }
  },
);

export const orderSlice = createSlice({
  name: "orderSlice",
  initialState: {
    orderHistory: [],
    loadedOrder: undefined,
    loadedOrderItems: [],
    shippingInfo: {
      combinations: [],
      preferred_rate: undefined,
      ship_complete_rate: undefined,
      two_day_rate: undefined,
      one_day_rate: undefined,
    },
    selectedReduxRate: undefined,
    collectingShippingInfo: false,
    shippingInfoError: null,
    shippingRatingProgress: 0,
    shippingRatingStatus: "idle",
    trackFee: 0,
    lastRatedWeight: 0,
    savingOrder: false,
    currentPeerNumber: 0,
    showLoader: false,
    holdDate: null,
    special_instructions: "",
    openConfirmationTrigger: false, // Added to manage confirmation trigger state
  },
  reducers: {
    setConfirmationTrigger: (state, action) => {
      state.openConfirmationTrigger = action.payload;
    },
    resetShippingInfo: (state) => {
      state.shippingInfo = {
        combinations: [],
        preferred_rate: undefined,
        ship_complete_rate: undefined,
        two_day_rate: undefined,
        one_day_rate: undefined,
      };
    },
    setSelectedRateRedux: (state, action) => {
      state.selectedReduxRate = action.payload;
    },
    setHoldDate: (state, action) => {
      state.holdDate = action.payload;
    },
    setSpecialInstructions: (state, action) => {
      state.special_instructions = action.payload;
    },
    setShippingRatingProgress: (state, action) => {
      state.shippingRatingProgress = action.payload.progress;
      state.shippingRatingStatus = action.payload.status;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrderHistory.fulfilled, (state, action) => {
        state.orderHistory = action.payload;
      })
      .addCase(getOrderById.pending, (state, action) => {
        state.showLoader = true;
      })
      .addCase(getOrderById.fulfilled, (state, action) => {
        const payload = action.payload;
        state.loadedOrder = payload;
        state.showLoader = false;
      })
      .addCase(getOrderById.rejected, (state, action) => {
        state.loadedOrder = [];
        state.showLoader = false;
      })
      .addCase(getOrderItemRepresentation.fulfilled, (state, action) => {
        state.loadedOrderItems = action.payload;
        state.showLoader = false;
      })
      .addCase(getOrderItemRepresentation.pending, (state, action) => {
        state.showLoader = true;
      })
      .addCase(getOrderItemRepresentation.rejected, (state, action) => {
        state.showLoader = false;
      })
      .addCase(getTrackFee.fulfilled, (state, action) => {
        state.trackFee = action.payload.track_fee;
      })
      .addCase(collectShippingInfo.pending, (state, action) => {
        state.collectingShippingInfo = true;
        state.shippingInfoError = null;
        state.shippingRatingProgress = 0;
        state.shippingRatingStatus = "pending";
      })
      .addCase(collectShippingInfo.rejected, (state, action) => {
        state.collectingShippingInfo = false;
        state.shippingInfoError = action.payload || {
          message: "Error collecting shipping information.",
        };
        state.shippingRatingStatus = "failed";
      })
      .addCase(collectShippingInfo.fulfilled, (state, action) => {
        state.shippingInfo = {
          ...state.shippingInfo,
          ...action.payload,
        };
        state.lastRatedWeight = action.payload.last_rated_weight;
        state.selectedReduxRate = action.payload.preferred_rate
          ? {
              type: "preferred_rate",
              data: action.payload.preferred_rate,
              isMultiWarehouse:
                Object.keys(action.payload.preferred_rate).length > 1,
            }
          : undefined;
        state.collectingShippingInfo = false;
        state.shippingInfoError = null;
        state.shippingRatingProgress = 100;
        state.shippingRatingStatus = "completed";
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.currentPeerNumber = action?.payload[0]?.peer_order_num;
        state.loadedOrder = undefined;
        state.loadedOrderItems = [];
        state.shippingInfo = {
          combinations: [],
          preferred_rate: undefined,
          ship_complete_rate: undefined,
          two_day_rate: undefined,
          one_day_rate: undefined,
        };
        state.selectedReduxRate = undefined;
        state.holdDate = null;
        state.special_instructions = "";
        state.savingOrder = false;
        state.showLoader = false;
        state.openConfirmationTrigger = true; // Trigger confirmation popup
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.savingOrder = false;
        state.showLoader = false;
      })
      .addCase(createOrder.pending, (state, action) => {
        state.savingOrder = true;
        state.showLoader = true;
      });
  },
});

export const {
  setSelectedRateRedux,
  setHoldDate,
  setSpecialInstructions,
  setConfirmationTrigger,
  resetShippingInfo,
  setShippingRatingProgress,
} = orderSlice.actions;

export default orderSlice.reducer;
